/* @flow */
import type { DeviceDetails, LinkedSensor } from './types'
import type { SelectorProps } from '../../util/redux'
import type { State } from '../../store/store'
import {
  deviceIsIrrigationController,
  isOneEnabledFlowSensor
} from '../../util/device'
import { locationSelector } from '../location/selectors'

export const deviceIdSelector = (state: State, props: SelectorProps) => {
  const location = locationSelector(state, props)
  if (location && location.devices) {
    const deviceInLocation = location.devices.find(deviceIsIrrigationController)
    if (deviceInLocation) {
      return deviceInLocation.id
    }
  }
  return null
}

export const deviceNameSelector = (
  state: State,
  props: SelectorProps
): ?string => {
  const location = locationSelector(state, props)
  if (location && location.devices) {
    const deviceInLocation = location.devices.find(deviceIsIrrigationController)
    if (deviceInLocation) {
      return deviceInLocation.name
    }
  }
  return null
}

export const deviceSelector = (state: State, props: SelectorProps) => {
  const deviceId = deviceIdSelector(state, props)
  return deviceId && state.device[deviceId] ? state.device[deviceId] : null
}

export const deviceDetailsSelector = (state: State, props: SelectorProps) => {
  const device = deviceSelector(state, props)
  return device && device.META && device.META.details
    ? device.META.details
    : null
}

export const hasEnabledFlowSensorSelector = (
  state: State,
  props: SelectorProps,
  deviceDetails?: ?DeviceDetails
) => {
  return (
    isOneEnabledFlowSensor(
      sensorOneSelector(state, props, deviceDetails),
      sensorTwoSelector(state, props, deviceDetails)
    ) || hasWirelessFlowSensorsSelector(state, props, deviceDetails)
  )
}

export const hasWirelessFlowSensorsSelector = (
  state: State,
  props: SelectorProps,
  deviceDetails?: ?DeviceDetails
) => {
  const controller = deviceDetails || deviceDetailsSelector(state, props)
  if (!controller || !controller.wirelessFlowSensorId) return false

  return controller.wirelessFlowSensorId.length > 0
}

const sensorSelectorHelper = (
  idKey: string,
  controller: ?DeviceDetails,
  reducer
): ?LinkedSensor => {
  if (!controller || !controller[idKey]) {
    return null
  }
  const sensorId = controller[idKey]
  const sensor = reducer[sensorId] ? reducer[sensorId].META.details : null
  return sensor && sensor.wiringPosition ? sensor : null
}

export const sensorOneSelector = (
  state: State,
  props: SelectorProps,
  deviceDetails?: ?DeviceDetails
): ?LinkedSensor => {
  return sensorSelectorHelper(
    'linkedSensorWiringPositionOneId',
    deviceDetails || deviceDetailsSelector(state, props),
    state.device
  )
}

export const sensorTwoSelector = (
  state: State,
  props: SelectorProps,
  deviceDetails?: ?DeviceDetails
): ?LinkedSensor => {
  return sensorSelectorHelper(
    'linkedSensorWiringPositionTwoId',
    deviceDetails || deviceDetailsSelector(state, props),
    state.device
  )
}

export const wirelessFlowSensorsSelector = (
  state: State,
  props: SelectorProps
) => {
  if (!hasWirelessFlowSensorsSelector(state, props)) return null

  // deviceDetailsSelector should never be null if hasWirelessFlowSensorsSelector, but add chacks for flow type checking
  const controller = deviceDetailsSelector(state, props) || {}

  // controller.wirelessFlowSensorId is an Array<string>
  const sensors = (controller.wirelessFlowSensorId || []).map(id =>
    state.device[id] ? state.device[id].META.details : null
  )

  /* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
   * v0.85.0. To view the error, delete this comment and run Flow. */
  return sensors.filter(sensor => Boolean(sensor))
}

export const deviceAlertsSelector = (state: State, props: SelectorProps) => {
  const device = deviceSelector(state, props)
  return device && device.META && device.META.alerts ? device.META.alerts : null
}

export const deviceZoneIdsSelector = (state: State, props: SelectorProps) => {
  const deviceId = deviceIdSelector(state, props)
  return deviceId &&
    state.device[deviceId] &&
    state.device[deviceId].META.zoneIds
    ? state.device[deviceId].META.zoneIds
    : null
}

export const zonesSelector = (state: State, props: SelectorProps) => {
  const device = deviceSelector(state, props)
  if (device && device.META.zoneIds) {
    /* \$FlowFixMe This comment suppresses an error found when upgrading Flow
     * to v0.85.0. To view the error, delete this comment and run Flow. */
    const zones = device.META.zoneIds
      .filter(zoneId => state.zone[zoneId] && state.zone[zoneId].created)
      .map(zoneId => state.zone[zoneId])
    // makes sure we have a zone for each of the device's zoneIds
    return zones.length === device.META.zoneIds.length ? zones : null
  } else {
    return null
  }
}

export const deviceStateSelector = (state: State, props: SelectorProps) => {
  const device = deviceSelector(state, props)
  return device ? device.META.state : null
}

export const deviceFeaturesSelector = (state: State, props: SelectorProps) => {
  const device = deviceSelector(state, props)
  return device ? device.META.features : null
}

export const desiredStateSelector = (state: State, props: SelectorProps) => {
  const deviceId = deviceIdSelector(state, props)
  const device = deviceId && state.device[deviceId]

  return device && device.META.state ? device.META.state.desiredState : null
}

export const serialNumberSelector = (state: State, props: SelectorProps) => {
  const deviceId = deviceIdSelector(state, props)
  const device = deviceId && state.device[deviceId]

  return device && device.META.details ? device.META.details.serialNumber : null
}

/**
 * Selector for whether or not to show the zone delay option
 * @param {object} state - Redux state
 * @param {object} props - Component props
 * @returns {boolean | null} - Whether or not to show the zone delay option
 */
export const showZoneDelayOptionSelector = (
  state: State,
  props: SelectorProps
): ?boolean => {
  const deviceDetails = deviceDetailsSelector(state, props)
  let showZoneDelayOption = null
  if (deviceDetails) {
    showZoneDelayOption = Boolean(deviceDetails.wellpumpDelayActive || false)
  }
  return showZoneDelayOption
}
